import React from 'react'

import { Eye, MoreVertical, Trash2 } from 'lucide-react'

import { PaginatedResponse } from '@/core/api/dtos/common/types'
import { Zone } from '@/core/api/entities/zone/zone'
import { useTranslation } from '@/core/i18n/useTranslation'
import {
	getBasicStatusBadge,
	useZoneStatus,
} from '@/features/zones/hooks/useZoneStatus'
import { AccountBadge } from '@/shared/components/atoms/AccountBadge/AccountBadge'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/atoms/Dropdown/Dropdown'
import { PaginationControls } from '@/shared/components/molecules/PaginationControls/PaginationControls'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'
import { cn } from '@/shared/utils/helpers'

interface SubscriptionTableProps {
	zones: PaginatedResponse<Zone> | undefined
	isLoading: boolean
	isFetching: boolean
	error: Error | null
	refetch: () => void
	onViewZoneDetails?: (zone: Zone) => void
	onDeleteZone?: (zone: Zone) => void
	currentPage: number
	totalPages: number
	pageSize: number
	onPageChange: (page: number) => void
	onPageSizeChange: (pageSize: number) => void
}

interface ZoneRowProps {
	zone: Zone
	onViewDetails?: (zone: Zone) => void
	onDelete?: (zone: Zone) => void
}

const ZoneRow: React.FC<ZoneRowProps> = ({ zone, onViewDetails, onDelete }) => {
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const currentAccount = useCurrentAccount()
	const { status } = useZoneStatus(zone)
	const statusBadge = getBasicStatusBadge(status, t)
	const StatusIcon = statusBadge.icon
	const showAccountBadge =
		!!zone.accountName && zone.accountId !== currentAccount?.id

	return (
		<tr className="text-content-tertiary">
			<td className="py-3 px-2">{zone.name}</td>
			<td className="py-3 px-2 hidden lg:table-cell">
				<div className="flex items-center gap-2 min-w-0">
					<span className="truncate">{zone.location?.name || '-'}</span>
					{showAccountBadge && <AccountBadge accountName={zone.accountName!} />}
				</div>
			</td>
			<td className="py-3 px-2">
				<Badge
					variant="zones"
					className={cn(
						statusBadge.className,
						'text-[14px] font-medium px-2 py-0.5 h-6 w-fit'
					)}
				>
					<StatusIcon className="h-4 w-4" />
					{statusBadge.label}
				</Badge>
			</td>
			<td className="py-3 px-2 hidden lg:table-cell">
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button
							className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-accent transition-colors"
							onClick={(e) => e.stopPropagation()}
						>
							<MoreVertical className="h-5 w-5 text-foreground" />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align="end"
						side="bottom"
						alignOffset={0}
						sideOffset={8}
						className="w-48"
					>
						<DropdownMenuItem
							onClick={(e) => {
								e.stopPropagation()
								onViewDetails?.(zone)
							}}
						>
							<Eye className="mr-2 h-4 w-4" />
							<span>{tCommon('account.zones.view_details')}</span>
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={(e) => {
								e.stopPropagation()
								onDelete?.(zone)
							}}
							variant="destructive"
						>
							<Trash2 className="mr-2 h-4 w-4" />
							<span>{tCommon('account.zones.delete_zone')}</span>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</td>
		</tr>
	)
}

const SubscriptionZonesTable: React.FC<SubscriptionTableProps> = ({
	zones,
	isLoading,
	onViewZoneDetails,
	onDeleteZone,
	currentPage,
	totalPages,
	pageSize,
	onPageChange,
	onPageSizeChange,
}) => {
	const { t } = useTranslation()
	return (
		<div className="height-full">
			<div className="space-y-4 mb-4">
				<div>
					<table className="w-full">
						<thead>
							<tr className="text-content-tertiary text-sm">
								<th className="text-left py-3 px-2">Name</th>
								<th className="text-left py-3 px-2 hidden lg:block">
									Location
								</th>
								<th className="text-left py-3 px-2">Status</th>
								<th className="text-left py-3 px-2 hidden lg:block">Actions</th>
							</tr>
						</thead>
						<tbody>
							{isLoading ? (
								<tr>
									<td
										colSpan={4}
										className="py-8 text-center text-content-tertiary"
									>
										{t('common.loading')}
									</td>
								</tr>
							) : zones?.data.length === 0 ? (
								<tr>
									<td
										colSpan={4}
										className={'py-8 text-center text-content-tertiary'}
									>
										{t('account.zones.no_zones')}
									</td>
								</tr>
							) : (
								zones?.data.map((zone) => (
									<ZoneRow
										key={zone.id}
										zone={zone}
										onViewDetails={onViewZoneDetails}
										onDelete={onDeleteZone}
									/>
								))
							)}
						</tbody>
					</table>
				</div>

				{/* Pagination Controls */}
				{!isLoading && zones && zones.data.length > 0 && (
					<PaginationControls
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={onPageChange}
						pageSize={pageSize}
						onPageSizeChange={onPageSizeChange}
					/>
				)}
			</div>
		</div>
	)
}

export default SubscriptionZonesTable
